import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { MarketingNav, Footer } from "@/components/Marketing";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr, copyText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, Loader2, Copy, Smartphone } from "lucide-react";

const TIERS = [
  { id: "free", name: "Free", price: "€0", period: "forever", desc: "For exploring Timeline.", cta: "Get started", features: ["3 active timelines", "Core layouts", "Public publishing", "Basic AI allowance", "Timeline branding"] },
  { id: "pro", name: "Pro", price: "€20", period: "/mo", desc: "For individual creators.", highlight: true, cta: "Upgrade to Pro", features: ["Unlimited timelines", "All premium layouts", "Advanced customization", "AI creation & copilot", "Analytics", "PDF & data export", "Remove branding", "Password protection"] },
  { id: "team", name: "Team", price: "€40", period: "/mo", desc: "For collaborative teams.", cta: "Upgrade to Team", features: ["Everything in Pro", "Multiple members", "Real-time collaboration", "Shared workspace", "Version history", "Team templates", "Brand Kit"] },
  { id: "business", name: "Business", price: "€60", period: "/mo", desc: "For organizations & institutions.", cta: "Upgrade to Business", features: ["Everything in Team", "Custom domains", "Advanced analytics", "SSO-ready", "White-label", "Admin controls", "Priority support"] },
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(null);
  const [config, setConfig] = useState({ enabled: false });
  const [upi, setUpi] = useState({ enabled: false });
  const [upiOpen, setUpiOpen] = useState(false);
  const [upiTier, setUpiTier] = useState(null);
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);
  const [methodTier, setMethodTier] = useState(null);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/billing/config"); setConfig(data); } catch {}
    try { const { data } = await api.get("/billing/upi"); setUpi(data); } catch {}
  })(); }, []);

  useEffect(() => {
    const status = params.get("paypal");
    if (!status) return;
    if (status === "success") {
      const subscription_id = params.get("subscription_id");
      const tier = params.get("tier");
      (async () => {
        try {
          await api.post("/billing/confirm", { subscription_id, tier });
          await refresh?.();
          toast.success(`You're on the ${tier?.charAt(0).toUpperCase()}${tier?.slice(1)} plan! 🎉`);
        } catch (e) { toast.error(apiErr(e)); }
      })();
    } else if (status === "cancel") {
      toast.info("Checkout cancelled.");
    }
    params.delete("paypal"); params.delete("subscription_id"); params.delete("tier"); params.delete("ba_token"); params.delete("token");
    setParams(params, { replace: true });
    // eslint-disable-next-line
  }, []);

  const startPaypal = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await api.post("/billing/subscribe", { tier });
      if (data.approve_url) window.location.href = data.approve_url;
      else toast.error("Could not start checkout.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const startUpi = (tier) => { setUpiTier(tier); setReference(""); setUpiOpen(true); };

  const upgrade = async (tier) => {
    if (tier === "free") { nav("/register"); return; }
    if (!user) { nav("/register"); return; }
    if (config.enabled && upi.enabled) { setMethodTier(tier); setMethodOpen(true); return; }
    if (config.enabled) { startPaypal(tier); return; }
    if (upi.enabled) { startUpi(tier); return; }
    toast.info("Payments aren't configured yet. Please check back soon.");
  };

  const submitUpi = async () => {
    setSubmitting(true);
    try {
      await api.post("/billing/upi/request", { tier: upiTier, reference });
      toast.success("Payment submitted! We'll activate your plan shortly.");
      setUpiOpen(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  const upiLink = upi.link ? `${upi.link}&am=${(upi.tiers_inr || {})[upiTier] || ""}` : "";
  const inrFmt = (n) => n ? Number(n).toLocaleString("en-IN") : "";

  return (
    <div className="bg-background min-h-screen">
      <MarketingNav />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Pricing</div>
          <h1 className="font-display text-5xl font-semibold tracking-tight text-foreground">Simple pricing for every storyteller</h1>
          <p className="mt-3 text-muted-foreground">From students to museums. Start free, upgrade when you're ready.{config.enabled && upi.enabled ? " Pay by PayPal, card, or UPI." : config.enabled ? " Pay securely with PayPal or card." : upi.enabled ? " Pay easily via UPI." : " Billed in EUR."}</p>
          {user && <p className="mt-2 text-sm">You're currently on the <span className="font-semibold text-primary capitalize">{user.subscription}</span> plan.</p>}
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((t) => {
            const current = user?.subscription === t.id;
            return (
              <div key={t.id} data-testid={`tier-${t.id}`}
                className={`rounded-2xl border p-7 flex flex-col ${t.highlight ? "border-primary bg-card shadow-[0_20px_60px_-20px_rgba(58,31,110,0.4)] relative" : "border-border bg-card"}`}>
                {t.highlight && <span className="absolute -top-3 left-7 text-[11px] font-semibold px-3 py-1 rounded-full bg-primary text-primary-foreground">Most popular</span>}
                <div className="font-display text-xl font-semibold text-foreground">{t.name}</div>
                <p className="text-sm text-muted-foreground mt-1">{t.desc}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-semibold text-foreground">{t.price}</span>
                  <span className="text-muted-foreground text-sm">{t.period}</span>
                </div>
                {upi.enabled && (upi.tiers_inr || {})[t.id] && (
                  <div className="mt-1 text-xs text-muted-foreground" data-testid={`tier-inr-${t.id}`}>≈ ₹{inrFmt((upi.tiers_inr || {})[t.id])} / mo via UPI</div>
                )}
                <Button onClick={() => upgrade(t.id)} disabled={busy === t.id || current}
                  className="mt-5 w-full rounded-full" variant={t.highlight ? "default" : "outline"} data-testid={`tier-cta-${t.id}`}>
                  {busy === t.id ? <Loader2 className="animate-spin" size={16} /> : current ? "Current plan" : t.cta}
                </Button>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-foreground/80"><Check size={16} className="text-primary mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        {!config.enabled && !upi.enabled && <p className="mt-10 text-center text-xs text-muted-foreground">Checkout activates once payment details are added.</p>}
        {!config.enabled && upi.enabled && <p className="mt-10 text-center text-xs text-muted-foreground flex items-center justify-center gap-1"><Smartphone size={13} /> Pay securely via UPI — scan a QR or use the UPI ID at checkout.</p>}
      </div>

      <Dialog open={upiOpen} onOpenChange={setUpiOpen}>
        <DialogContent data-testid="upi-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Pay via UPI</DialogTitle>
            <DialogDescription>Scan the QR or pay to the UPI ID below, then submit your transaction reference. We'll activate your <span className="capitalize font-medium">{upiTier}</span> plan shortly.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            {upiLink && (
              <div className="p-4 rounded-2xl bg-white border border-border" data-testid="upi-qr">
                <QRCodeSVG value={upiLink} size={180} fgColor="#3A1F6E" />
              </div>
            )}
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Amount to pay</div>
              <div className="font-display text-3xl font-semibold text-foreground" data-testid="upi-amount-inr">₹{inrFmt((upi.tiers_inr || {})[upiTier])}</div>
              <div className="text-xs text-muted-foreground">≈ €{(upi.tiers || {})[upiTier]} / mo</div>
            </div>
            <div className="w-full flex items-center gap-2 rounded-lg border border-border p-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Pay to · {upi.name}</div>
                <span className="text-sm text-foreground truncate block" data-testid="upi-id">{upi.upi_id}</span>
              </div>
              <Button size="sm" variant="outline" onClick={() => { copyText(upi.upi_id); toast.success("UPI ID copied"); }} data-testid="copy-upi-btn"><Copy size={14} className="mr-1" /> Copy</Button>
            </div>
            <div className="w-full">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Transaction reference / UTR (optional)</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 4012 3456 7890" data-testid="upi-reference" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitUpi} disabled={submitting} className="rounded-full w-full" data-testid="submit-upi-btn">{submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null} I've paid — submit for approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={methodOpen} onOpenChange={setMethodOpen}>
        <DialogContent data-testid="method-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Choose payment method</DialogTitle>
            <DialogDescription>Upgrade to the <span className="capitalize font-medium">{methodTier}</span> plan with your preferred option.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <button onClick={() => { setMethodOpen(false); startPaypal(methodTier); }} data-testid="method-paypal"
              className="flex items-center gap-3 rounded-xl border border-border p-4 text-left hover:border-primary/50 transition-colors">
              <span className="h-10 w-10 grid place-items-center rounded-lg bg-[#003087] text-white font-bold text-sm shrink-0">PP</span>
              <div><div className="font-medium text-foreground">PayPal / Card</div><div className="text-xs text-muted-foreground">Instant automatic activation · billed in €</div></div>
            </button>
            <button onClick={() => { setMethodOpen(false); startUpi(methodTier); }} data-testid="method-upi"
              className="flex items-center gap-3 rounded-xl border border-border p-4 text-left hover:border-primary/50 transition-colors">
              <span className="h-10 w-10 grid place-items-center rounded-lg bg-primary/10 text-primary shrink-0"><Smartphone size={20} /></span>
              <div><div className="font-medium text-foreground">UPI (India)</div><div className="text-xs text-muted-foreground">Scan a QR · ₹{inrFmt((upi.tiers_inr || {})[methodTier])} · manual approval</div></div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
